import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import apiClient from '@/api/client'
import { useDashboardData } from '@/hooks/useDashboardData'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'
import { useKitchenSettings, isKDSEnabled } from '@/hooks/useKitchenSettings'
import type { DashboardPeriod } from '@/types'
import { DashboardHeader } from './dashboard/DashboardHeader'
import { KpiHeroRow } from './dashboard/KpiHeroRow'
import { dashboardWindowToReportSearch } from './dashboard/dashboardFormat'
import type { ReportTarget } from './reports/ReportsHeadline'
import { LivePulseStrip } from './dashboard/LivePulseStrip'
import { AlertsPanel } from '@/components/admin/dashboard/AlertsPanel'
import { AgingOpenOrdersAlert } from './AgingOpenOrdersAlert'
import { SalesTimeseriesChart } from './dashboard/SalesTimeseriesChart'
import { RevenueProfitPanel } from './dashboard/RevenueProfitPanel'
import { TopItemsList } from './dashboard/TopItemsList'
import { PaymentMixDonut } from './dashboard/PaymentMixDonut'
import { OrderTypeMixBar } from './dashboard/OrderTypeMixBar'
import { ActivityFeed } from './dashboard/ActivityFeed'

interface AdminDashboardProps {
  /**
   * Optional override used by the legacy state-based AdminLayout to swap
   * sections via a setter. The TanStack-router admin shell mounts this
   * component without the prop — in that case we fall back to URL
   * navigation through `useRouter()`.
   */
  onNavigate?: (section: string) => void
}

// Map a dashboard section id (e.g. "counter", "void-log") to its admin
// route. All admin routes live under /admin/<id>; if any new section ever
// needs a non-trivial path, override it here.
function sectionToPath(section: string): string {
  return `/admin/${section}`
}

const PERIOD_LABELS: Record<DashboardPeriod, string> = {
  today: 'Yesterday',
  yesterday: 'Day before',
  '7d': 'Prior 7d',
  '30d': 'Prior 30d',
  cw: 'Last week',
  cm: 'Last month',
  custom: 'Prior window',
}

/**
 * AdminDashboard — the redesigned restaurant operations cockpit.
 *
 * Sections (top to bottom):
 *   1. Sticky header: title + Live status + period selector + Refresh /
 *      Reports / Settings buttons.
 *   2. Live Pulse strip — SSE-driven cards for active orders, kitchen,
 *      tables, voids, drawer.
 *   3. KPI hero row — gross sales, orders, avg order value, guests,
 *      refunds issued, gross sales after refunds (with prior-period deltas).
 *   4. Sales chart + Activity feed.
 *   5. Revenue & profit panel.
 *   6. Top items + Payment mix + Order type mix.
 */
export function AdminDashboard({ onNavigate }: AdminDashboardProps) {
  const router = useRouter()
  const kitchen = useKitchenSettings()
  const isKDS = isKDSEnabled(kitchen.mode)
  // Operational alerts. Polled rather than SSE-driven: these are slow-moving
  // conditions (low stock, an unreconciled drawer, guests awaiting a reply),
  // not per-order events.
  const alertsQuery = useQuery({
    queryKey: ['dashboard', 'alerts'],
    queryFn: () => apiClient.getDashboardAlerts(),
    staleTime: 60_000,
    refetchInterval: 120_000,
  })
  // Default navigation path: TanStack Router URL change. The prop override
  // is only used by the legacy `components/admin/AdminLayout.tsx` switcher.
  const navigate = useCallback(
    (section: string) => {
      if (onNavigate) {
        onNavigate(section)
        return
      }
      router.navigate({ to: sectionToPath(section) })
    },
    [onNavigate, router],
  )
  const [period, setPeriod] = useState<DashboardPeriod>('today')


  const data = useDashboardData({ period })

  // F-FE-N205: a KPI tile opens the report behind it for the window the
  // dashboard is showing (the server-resolved business dates, not a preset
  // re-computed on the other page).
  const openReport = useCallback(
    (target: ReportTarget) => {
      const ov = data.overview.data
      const range = ov ? dashboardWindowToReportSearch(period, ov.from, ov.to) : {}
      if (target === 'pnl' || target === 'ledger') {
        router.navigate({ to: '/admin/expenses', search: { tab: target, ...range } as never })
        return
      }
      const singleDay = 'from' in range && range.from === range.to
      router.navigate({
        to: '/admin/reports',
        search: {
          ...range,
          tab: target === 'orders' ? 'daily' : target,
          ...(target === 'orders' && singleDay ? { day: ov?.to } : {}),
        } as never,
      })
    },
    [router, period, data.overview.data],
  )

  // Keyboard shortcuts (T/Y/W/M/R) for power users — match common
  // dashboards like Toast & Square.
  useKeyboardShortcuts({
    shortcuts: useMemo(
      () => [
        { key: 't', action: () => setPeriod('today'), description: 'Today' },
        { key: 'y', action: () => setPeriod('yesterday'), description: 'Yesterday' },
        { key: 'w', action: () => setPeriod('cw'), description: 'This week' },
        { key: 'm', action: () => setPeriod('cm'), description: 'This month' },
        { key: 'r', action: () => data.refetchAll(), description: 'Refresh' },
      ],
      [data],
    ),
  })

  const overview = data.overview.data
  const isAnyFetching =
    data.overview.isFetching ||
    data.live.isFetching ||
    data.timeseries.isFetching

  return (
    <div className="space-y-6 px-4 py-5 sm:p-6">
      <DashboardHeader
        period={period}
        onPeriodChange={setPeriod}
        onRefresh={data.refetchAll}
        onNavigate={navigate}
        streamStatus={data.streamStatus}
        isRefreshing={isAnyFetching}
        fromISO={overview?.from}
        toISO={overview?.to}
      />

      <LivePulseStrip pulse={data.live.data} isLoading={data.live.isLoading} onNavigate={navigate} isKDS={isKDS} />

      <AgingOpenOrdersAlert variant="tile" />

      {/* Operational alerts. The panel and its endpoint both already existed
          but were never mounted, so every alert kind — low stock, stale
          tickets, an unreconciled drawer — has been computed and thrown away.
          Mounted here for the guest-feedback alert; the rest come along with
          it. */}
      <AlertsPanel
        alerts={alertsQuery.data?.data}
        isLoading={alertsQuery.isLoading}
        onNavigate={navigate}
      />

      <KpiHeroRow
        overview={overview}
        isLoading={data.overview.isLoading}
        previousLabel={PERIOD_LABELS[period]}
        onOpen={openReport}
      />

      <div className="grid min-w-0 gap-6 md:grid-cols-[2fr_1fr]">
        <SalesTimeseriesChart
          data={data.timeseries.data}
          isLoading={data.timeseries.isLoading}
          priorLabel={PERIOD_LABELS[period]}
        />
        <ActivityFeed
          entries={data.activity.entries}
          businessDate={data.activity.businessDate}
          isLoading={data.activity.isLoading}
          isError={data.activity.isError}
          hasMore={data.activity.hasMore}
          isFetchingMore={data.activity.isFetchingMore}
          onLoadMore={data.activity.loadMore}
        />
      </div>

      <RevenueProfitPanel overview={overview} isLoading={data.overview.isLoading} />

      <div className="grid min-w-0 gap-6 md:grid-cols-2 xl:grid-cols-3">
        <TopItemsList items={data.topItems.data} isLoading={data.topItems.isLoading} />
        <PaymentMixDonut data={data.paymentMix.data} isLoading={data.paymentMix.isLoading} />
        <OrderTypeMixBar data={data.orderTypeMix.data} isLoading={data.orderTypeMix.isLoading} />
      </div>
    </div>
  )
}
